// ============================================================
// ProgressDashboard.tsx — 員工個人進度儀表板
// 顯示：分數趨勢折線圖、問題分析、課程完成率、歷史記錄
// ============================================================

import React, { FC, useEffect, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { StaffProgress, PostureIssueType } from 'types/posture';
import { usePostureSession }              from 'hooks/usePostureSession';
import { GRADE_COLORS }                  from 'utils/poseConstants';

// ── 問題類型中文對照 ─────────────────────────────────────
const ISSUE_LABELS: Record<PostureIssueType, string> = {
    HEAD_FORWARD:    '頭部前傾',
    SHOULDER_UNEVEN: '肩膀不水平',
    SPINE_TILT:      '脊椎側傾',
    ROUNDED_BACK:    '圓背/駝背',
    PELVIC_TILT:     '骨盆傾斜',
    KNEE_LOCKED:     '膝蓋鎖死',
    WEIGHT_SHIFT:    '重心偏移',
};

// ── Props 定義 ───────────────────────────────────────────
interface ProgressDashboardProps {
    staffId:   number;
    staffName: string;
}

// ── 統計卡片元件 ─────────────────────────────────────────
interface StatCardProps {
    label:     string;
    value:     string | number;
    icon:      string;
    subtext?:  string;
    highlight?: boolean;
}

const StatCard: FC<StatCardProps> = ({ label, value, icon, subtext, highlight }) => (
    <div style={{
        background:   highlight ? 'rgba(0,170,255,0.15)' : 'rgba(255,255,255,0.05)',
        border:       `1px solid ${highlight ? '#00AAFF' : '#2a2a3e'}`,
        borderRadius: '12px',
        padding:      '16px 20px',
        flex:         1,
        minWidth:     '120px',
    }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
        <div style={{
            fontSize:   28,
            fontWeight: 700,
            color:      highlight ? '#00AAFF' : '#fff',
            lineHeight: 1,
        }}>
            {value}
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{label}</div>
        {subtext && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{subtext}</div>
        )}
    </div>
);

// ── 自訂 Tooltip（折線圖） ───────────────────────────────
interface CustomTooltipProps {
    active?:  boolean;
    payload?: { value: number }[];
    label?:   string;
}

const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const score = payload[0].value;
    const grade =
        score >= 90 ? 'S' :
            score >= 80 ? 'A' :
                score >= 70 ? 'B' :
                    score >= 60 ? 'C' : 'D';

    return (
        <div style={{
            background:   '#1a1a2e',
            border:       '1px solid #333',
            borderRadius: '8px',
            padding:      '10px 14px',
        }}>
            <p style={{ color: '#888', fontSize: 11, margin: '0 0 4px' }}>{label}</p>
            <p style={{ color: '#fff', fontWeight: 700, fontSize: 16, margin: 0 }}>
                {score} 分
                <span style={{
                    marginLeft:   8,
                    fontSize:     12,
                    color:        score >= 80 ? '#00FF88' : score >= 60 ? '#FF8C00' : '#FF4444',
                }}>
          ({grade} 級)
        </span>
            </p>
        </div>
    );
};

// ── 主元件 ───────────────────────────────────────────────
const ProgressDashboard: FC<ProgressDashboardProps> = ({ staffId, staffName }) => {
    const { progress, fetchProgress, downloadReport, isSubmitting } = usePostureSession();
    const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');

    // 掛載時獲取進度數據
    useEffect(() => {
        fetchProgress(staffId);
    }, [staffId, fetchProgress]);

    // console.log(progress)
    // ── 載入中 ─────────────────────────────────────────────
    if (!progress) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: 300, flexDirection: 'column', gap: 12,
            }}>
                <div className="loading-spinner" />
                <p style={{ color: '#888' }}>載入進度數據中...</p>
            </div>
        );
    }

    // ── 尚無記錄 ───────────────────────────────────────────
    if (progress.totalSessions === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <p style={{ fontSize: 48 }}>🎯</p>
                <h3 style={{ color: '#fff' }}>尚未進行過訓練</h3>
                <p style={{ color: '#888' }}>完成第一次姿勢評估後，進度數據將顯示於此</p>
            </div>
        );
    }

    // ── 週進步文字 ────────────────────────────────────────
    const weeklyText =
        progress.weeklyImprovement > 0
            ? `↑ +${progress.weeklyImprovement} 分（較上週進步）`
            : progress.weeklyImprovement < 0
                ? `↓ ${progress.weeklyImprovement} 分（較上週下降）`
                : '與上週持平';

    const weeklyColor =
        progress.weeklyImprovement > 0 ? '#00FF88' :
            progress.weeklyImprovement < 0 ? '#FF4444' : '#888';

    // ── 圖表數據（最近 20 筆，避免折線圖過密） ────────────
    const chartData = progress.scoreHistory?.slice(-20).map(item => ({
        date:  item.date.slice(5),    // 只顯示 MM-DD
        score: item.score,
    })) ?? [];

    return (
        <div className="progress-dashboard" style={{ color: '#fff' }}>

            {/* ── 標題列 ────────────────────────────────────── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>📊 {staffName} 的訓練進度</h2>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: 12 }}>
                        共完成 {progress.totalSessions} 次訓練
                    </p>
                </div>
                <button
                    onClick={() => downloadReport(staffId)}
                    disabled={isSubmitting}
                    style={{
                        padding:      '8px 16px',
                        background:   'rgba(0,170,255,0.2)',
                        border:       '1px solid #00AAFF',
                        borderRadius: '8px',
                        color:        '#00AAFF',
                        cursor:       'pointer',
                        fontSize:     12,
                    }}
                >
                    📥 下載個人報告
                </button>
            </div>

            {/* ── 統計卡片 ──────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <StatCard
                    icon="🎯" label="平均評分"
                    value={progress.averageScore}
                    subtext={weeklyText}
                    highlight
                />
                <StatCard
                    icon="🏅" label="最新評級"
                    value={progress.latestGrade ?? 'N/A'}
                />
                <StatCard
                    icon="📚" label="完成課程"
                    value={`${progress.completedModules.length} / 4`}
                    subtext="個學習模組"
                />
                <StatCard
                    icon="⚠️" label="最常見問題"
                    value={progress.mostCommonIssue
                        ? ISSUE_LABELS[progress.mostCommonIssue]
                        : '無'}
                    subtext="需重點練習"
                />
            </div>

            {/* ── 分頁切換 ──────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {(['overview', 'history'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding:      '8px 20px',
                            borderRadius: '20px',
                            border:       `1px solid ${activeTab === tab ? '#00AAFF' : '#333'}`,
                            background:   activeTab === tab ? 'rgba(0,170,255,0.2)' : 'none',
                            color:        activeTab === tab ? '#00AAFF' : '#888',
                            cursor:       'pointer',
                            fontSize:     13,
                        }}
                    >
                        {tab === 'overview' ? '📈 分數趨勢' : '📋 訓練記錄'}
                    </button>
                ))}
            </div>

            {/* ── 總覽頁 ────────────────────────────────────── */}
            {activeTab === 'overview' && (
                <>
                    {/* 分數趨勢折線圖 */}
                    <div style={{
                        background:   'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        padding:      '20px',
                        marginBottom: '16px',
                    }}>
                        <h4 style={{ margin: '0 0 16px', color: '#aaa', fontSize: 13 }}>
                            評分趨勢（最近 {chartData.length} 次）
                        </h4>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fill: '#666', fontSize: 10 }}
                                    tickLine={false}
                                    axisLine={{ stroke: '#2a2a3e' }}
                                />
                                <YAxis
                                    domain={[0, 100]}
                                    tick={{ fill: '#666', fontSize: 10 }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                {/* 各評級分隔線 */}
                                <ReferenceLine y={90} stroke="#FFD700" strokeDasharray="4 4"
                                               label={{ value: 'S', fill: '#FFD700', fontSize: 10 }} />
                                <ReferenceLine y={80} stroke="#00FF88" strokeDasharray="4 4"
                                               label={{ value: 'A', fill: '#00FF88', fontSize: 10 }} />
                                <ReferenceLine y={70} stroke="#00AAFF" strokeDasharray="4 4"
                                               label={{ value: 'B', fill: '#00AAFF', fontSize: 10 }} />
                                <ReferenceLine y={60} stroke="#FF8C00" strokeDasharray="4 4"
                                               label={{ value: 'C', fill: '#FF8C00', fontSize: 10 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Line
                                    type="monotone"
                                    dataKey="score"
                                    stroke="#00AAFF"
                                    strokeWidth={2}
                                    dot={{ fill: '#00AAFF', r: 3, strokeWidth: 0 }}
                                    activeDot={{ r: 6, fill: '#fff', stroke: '#00AAFF', strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* 課程完成進度條 */}
                    <div style={{
                        background:   'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        padding:      '20px',
                    }}>
                        <h4 style={{ margin: '0 0 16px', color: '#aaa', fontSize: 13 }}>
                            學習課程完成進度
                        </h4>
                        {[
                            { id: 'module_1_basics',          label: 'Module 1：姿勢科學基礎' },
                            { id: 'module_2_service_posture',  label: 'Module 2：前台服務站姿' },
                            { id: 'module_3_standing_health',  label: 'Module 3：久站護理' },
                            { id: 'module_4_correction',       label: 'Module 4：常見問題矯正' },
                        ].map(module => {
                            const done = progress.completedModules.includes(module.id);
                            return (
                                <div key={module.id} style={{
                                    display:       'flex',
                                    alignItems:    'center',
                                    gap:           12,
                                    marginBottom:  10,
                                }}>
                                    <span style={{ fontSize: 16 }}>{done ? '✅' : '⬜'}</span>
                                    <span style={{ fontSize: 13, color: done ? '#fff' : '#666', flex: 1 }}>
                    {module.label}
                  </span>
                                    {done && (
                                        <span style={{
                                            fontSize:     10,
                                            color:        '#00FF88',
                                            border:       '1px solid #00FF88',
                                            borderRadius: '10px',
                                            padding:      '2px 8px',
                                        }}>
                      已完成
                    </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* ── 訓練記錄頁 ───────────────────────────────── */}
            {activeTab === 'history' && (
                <div style={{
                    background:   'rgba(255,255,255,0.03)',
                    borderRadius: '12px',
                    overflow:     'hidden',
                }}>
                    {/* 表頭 */}
                    <div style={{
                        display:    'grid',
                        gridTemplateColumns: '1fr 80px 60px 80px',
                        padding:    '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        fontSize:   12,
                        color:      '#888',
                        fontWeight: 600,
                    }}>
                        <span>日期</span>
                        <span style={{ textAlign: 'center' }}>評分</span>
                        <span style={{ textAlign: 'center' }}>評級</span>
                        <span style={{ textAlign: 'center' }}>時長</span>
                    </div>

                    {/* 歷史記錄列表（倒序顯示） */}
                    {progress.scoreHistory.slice().reverse().map((item, idx) => {
                        const grade =
                            item.score >= 90 ? 'S' :
                                item.score >= 80 ? 'A' :
                                    item.score >= 70 ? 'B' :
                                        item.score >= 60 ? 'C' : 'D';
                        const scoreColor =
                            item.score >= 80 ? '#00FF88' :
                                item.score >= 60 ? '#FF8C00' : '#FF4444';

                        return (
                            <div
                                key={idx}
                                style={{
                                    display:    'grid',
                                    gridTemplateColumns: '1fr 80px 60px 80px',
                                    padding:    '12px 16px',
                                    borderTop:  '1px solid #1a1a2e',
                                    fontSize:   13,
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                <span style={{ color: '#ccc' }}>
                  {new Date(item.date).toLocaleDateString('zh-TW', {
                      month: 'long', day: 'numeric'
                  })}
                </span>
                                <span style={{ textAlign: 'center', color: scoreColor, fontWeight: 700 }}>
                  {item.score}
                </span>
                                <span style={{ textAlign: 'center' }}>
                  <span style={{
                      fontSize:     12,
                      padding:      '2px 8px',
                      borderRadius: '10px',
                      background:   `${scoreColor}22`,
                      color:        scoreColor,
                      fontWeight:   700,
                  }}>
                    {grade}
                  </span>
                </span>
                                <span style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
                  —
                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProgressDashboard;
